import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  message,
  Card,
  Tag,
  Spin,
  Alert,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  MessageOutlined,
  SendOutlined,
} from "@ant-design/icons";
import Logo from "../components/general/Logo";
import { device } from "../lib/breakpoints";
import { getApiUrl } from "../lib/api/config";
import LeoVerifiedBidBadge from "../components/vendor/LeoVerifiedBidBadge";
import PreferredEdgePromo from "../components/vendor/PreferredEdgePromo";

const { TextArea } = Input;
const { Option } = Select;

const VENDOR_CHAT_POLL_MS = 30_000;

function formatVendorRole(role) {
  if (role === "leo") return "Clumse AI";
  if (role === "vendor") return "You";
  return String(role || "System");
}

/** Minimal markdown: **bold** and line breaks — no HTML injection */
function VendorMessageBody({ text, isBlueprint }) {
  const safe = String(text || "");
  const lines = safe.split("\n");
  return (
    <MessageBody $isBlueprint={isBlueprint}>
      {lines.map((line, li) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <span key={li}>
            {li > 0 ? <br /> : null}
            {parts.map((part, pi) => {
              if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={pi}>{part.slice(2, -2)}</strong>;
              }
              return <span key={pi}>{part}</span>;
            })}
          </span>
        );
      })}
    </MessageBody>
  );
}

function negotiationLogSignature(log) {
  if (!Array.isArray(log) || !log.length) return "";
  const last = log[log.length - 1];
  return `${log.length}:${last?.createdAt || ""}:${String(last?.content || "").slice(0, 40)}`;
}

const VendorBidPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [existingBid, setExistingBid] = useState(null);
  const [error, setError] = useState(null);
  const [negotiationLog, setNegotiationLog] = useState([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [isPreferredVendor, setIsPreferredVendor] = useState(false);
  const [isPrivacyMasked, setIsPrivacyMasked] = useState(false);
  const [locationLabel, setLocationLabel] = useState("");
  const chatScrollRef = React.useRef(null);
  const negotiationSigRef = React.useRef("");

  const loadBidData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/vendor-bids/bid/${token}`);

      if (!response.ok) {
        if (response.status === 404) {
          setError(
            "Invalid or expired link. Please contact Clumse support for a new invite."
          );
        } else {
          setError("Failed to load ticket details. Please try again later.");
        }
        return;
      }

      const data = await response.json();
      setTicketData(data.ticket);
      setVendorData(data.vendor);
      setExistingBid(data.existingBid);
      const log = data.negotiation_log || [];
      setNegotiationLog(log);
      negotiationSigRef.current = negotiationLogSignature(log);
      setIsPrivacyMasked(Boolean(data.isPrivacyMasked ?? data.ticket?.isPrivacyMasked));
      setLocationLabel(
        String(data.locationLabel || data.ticket?.location || "").trim()
      );
      setIsPreferredVendor(
        Boolean(data.isPreferredVendor ?? data.vendor?.isPreferred)
      );

      if (data.vendor) {
        form.setFieldsValue({
          vendorName: data.vendor.name,
          vendorEmail: data.vendor.email,
          vendorPhone: data.vendor.phone || "",
        });
      }
    } catch (err) {
      console.error("Error fetching ticket data:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [token, form]);

  useEffect(() => {
    loadBidData();
  }, [loadBidData]);

  useEffect(() => {
    const label = isPrivacyMasked
      ? `Clumse — ${String(ticketData?.category || "Project").replace(/_/g, " ")} bid`
      : "Clumse — Submit bid";
    document.title = label;
    return () => {
      document.title = "Clumse";
    };
  }, [isPrivacyMasked, ticketData?.category]);

  useEffect(() => {
    if (!token || !isPrivacyMasked) return undefined;
    const poll = async () => {
      if (chatDraft.trim() || chatSending) return;
      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/vendor-bids/bid/${token}`);
        if (!response.ok) return;
        const data = await response.json();
        const nextLog = data.negotiation_log || [];
        const sig = negotiationLogSignature(nextLog);
        if (sig !== negotiationSigRef.current) {
          negotiationSigRef.current = sig;
          setNegotiationLog(nextLog);
        }
      } catch {
        /* non-fatal */
      }
    };
    const id = setInterval(() => void poll(), VENDOR_CHAT_POLL_MS);
    return () => clearInterval(id);
  }, [token, isPrivacyMasked, chatDraft, chatSending]);

  const sendLeoMessage = async () => {
    const trimmed = chatDraft.trim();
    if (!trimmed || !token) return;
    try {
      setChatSending(true);
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/vendor-bids/bid/${token}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Could not send message");
      }
      setNegotiationLog(data.negotiation_log || []);
      negotiationSigRef.current = negotiationLogSignature(
        data.negotiation_log || []
      );
      setChatDraft("");
    } catch (err) {
      console.error(err);
      message.error(err.message || "Chat failed");
    } finally {
      setChatSending(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const apiUrl = getApiUrl();

      const bidData = {
        token,
        bidAmount: values.bidAmount,
        estimatedHours: values.estimatedHours,
        timeline: values.timeline,
        notes: values.notes,
        phone: values.vendorPhone,
      };

      const response = await fetch(`${apiUrl}/vendor-bids/bids`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bidData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to submit bid");
      }

      message.success("Bid submitted successfully!");

      // Navigate to success page
      navigate("/bid/success", {
        state: {
          ticketTitle: ticketData.title,
          bidAmount: values.bidAmount,
          ticketId: ticketData?.id,
          workOrderId: ticketData?.id,
        },
      });
    } catch (err) {
      console.error("Error submitting bid:", err);
      message.error(err.message || "Failed to submit bid. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "processing";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getBidStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "processing";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <Spin size="large" />
          <LoadingText>Loading bid details...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <Logo size="large" displayMode="both" />
          <Alert
            message="Unable to Load Bid"
            description={error}
            type="error"
            showIcon
            style={{ marginTop: "2rem", maxWidth: "600px" }}
          />
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (existingBid) {
    return (
      <PageContainer>
        <VendorBidLayout>
          <MainCol>
        <Container>
          <Header>
            <Logo size="large" displayMode="both" />
            <HeaderTitle>Bid Already Submitted</HeaderTitle>
            <HeaderSubtitle>
              You've already submitted a bid for this repair
            </HeaderSubtitle>
          </Header>

          <Card
            title={
              <span>
                <MessageOutlined style={{ marginRight: 8 }} />
                Ask Clumse AI
              </span>
            }
            extra={isPreferredVendor ? <LeoVerifiedBidBadge /> : null}
            style={{ marginBottom: "2rem" }}
          >
            <ChatScroll ref={chatScrollRef}>
              {negotiationLog.length === 0 ? (
                <TextMuted>No messages in this thread yet.</TextMuted>
              ) : (
                negotiationLog.map((m, i) => (
                  <ChatBubble key={`${m.createdAt || i}-${i}`} $role={m.role}>
                    <ChatRole>{formatVendorRole(m.role)}</ChatRole>
                    <VendorMessageBody
                      text={m.content}
                      isBlueprint={m.messageType === "blueprint" || (i === 0 && m.role === "leo")}
                    />
                  </ChatBubble>
                ))
              )}
            </ChatScroll>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <TextArea
                rows={2}
                value={chatDraft}
                onChange={(e) => setChatDraft(e.target.value)}
                placeholder="Follow up with Clumse AI about this job…"
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    sendLeoMessage();
                  }
                }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={chatSending}
                onClick={sendLeoMessage}
                style={{ alignSelf: "flex-end" }}
              >
                Send
              </Button>
            </div>
          </Card>

          <Card>
            <ExistingBidSection>
              <SectionTitle>Your Existing Bid</SectionTitle>
              <BidStatusRow>
                <strong>Status:</strong>
                <Tag
                  color={getBidStatusColor(existingBid.status)}
                  style={{ marginLeft: "0.5rem" }}
                >
                  {existingBid.status.toUpperCase()}
                </Tag>
              </BidStatusRow>
              <BidDetailRow>
                <strong>Bid Amount:</strong> $
                {existingBid.bidAmount?.toLocaleString()}
              </BidDetailRow>
              {existingBid.estimatedHours && (
                <BidDetailRow>
                  <strong>Estimated Hours:</strong> {existingBid.estimatedHours}{" "}
                  hours
                </BidDetailRow>
              )}
              {existingBid.timeline && (
                <BidDetailRow>
                  <strong>Timeline:</strong> {existingBid.timeline}
                </BidDetailRow>
              )}
              {existingBid.notes && (
                <BidDetailRow>
                  <strong>Notes:</strong> {existingBid.notes}
                </BidDetailRow>
              )}
              <BidDetailRow>
                <strong>Submitted:</strong>{" "}
                {new Date(existingBid.createdAt).toLocaleString()}
              </BidDetailRow>
            </ExistingBidSection>
          </Card>
        </Container>
          </MainCol>
          <AsideCol>
            <PreferredEdgePromo
              bidToken={token}
              returnPage="bid"
              isPreferred={isPreferredVendor}
              onPreferredChange={setIsPreferredVendor}
            />
          </AsideCol>
        </VendorBidLayout>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <VendorBidLayout>
        <MainCol>
      <Container>
        <Header>
          <Logo size="large" displayMode="both" />
          {locationLabel ? <ZipBadge>{locationLabel}</ZipBadge> : null}
          <HeaderTitle>
            {isPrivacyMasked ? "Submit Your Quote" : "Submit Your Bid"}
          </HeaderTitle>
          <HeaderSubtitle>
            {isPrivacyMasked
              ? "Private Clumse AI channel — ZIP-only location"
              : "New Maintenance Opportunity"}
          </HeaderSubtitle>
        </Header>

        <Card
          title={isPrivacyMasked ? "Project scope" : "Repair Details"}
          style={{ marginBottom: "2rem" }}
        >
          <TicketDetailRow>
            <strong>
              <ToolOutlined /> {isPrivacyMasked ? "Trade:" : "Repair:"}
            </strong>{" "}
            {ticketData?.title}
          </TicketDetailRow>
          <TicketDetailRow>
            <strong>Category:</strong>
            <Tag style={{ marginLeft: "0.5rem" }}>{ticketData?.category}</Tag>
          </TicketDetailRow>
          {!isPrivacyMasked ? (
            <TicketDetailRow>
              <strong>Priority:</strong>
              <Tag
                color={getPriorityColor(ticketData?.priority)}
                style={{ marginLeft: "0.5rem" }}
              >
                {ticketData?.priority || "medium"}
              </Tag>
            </TicketDetailRow>
          ) : null}
          {!isPrivacyMasked && ticketData?.unitNumber ? (
            <TicketDetailRow>
              <strong>
                <HomeOutlined /> Unit:
              </strong>{" "}
              {ticketData.unitNumber}
            </TicketDetailRow>
          ) : null}
          {ticketData?.description ? (
            <TicketDetailRow>
              <strong>{isPrivacyMasked ? "Blueprint:" : "Description:"}</strong>
              {isPrivacyMasked ? (
                <BlueprintPanel>
                  <VendorMessageBody text={ticketData.description} isBlueprint />
                </BlueprintPanel>
              ) : (
                <Description>{ticketData.description}</Description>
              )}
            </TicketDetailRow>
          ) : null}
          {!isPrivacyMasked && ticketData?.estimatedCost > 0 ? (
            <TicketDetailRow>
              <strong>
                <DollarOutlined /> Estimated Cost:
              </strong>{" "}
              ${ticketData.estimatedCost.toLocaleString()}
            </TicketDetailRow>
          ) : null}
        </Card>

        {!isPrivacyMasked && token ? (
          <NegotiateLinkRow>
            <Link to={`/negotiate/${token}`}>
              Open Clumse AI bid chat (ZIP check, ETA & bid workflow)
            </Link>
          </NegotiateLinkRow>
        ) : null}

        <Card
          title={
            <span>
              <MessageOutlined style={{ marginRight: 8 }} />
              Ask Clumse AI about this job
            </span>
          }
          extra={isPreferredVendor ? <LeoVerifiedBidBadge /> : null}
          style={{ marginBottom: "2rem" }}
        >
          <p style={{ color: "var(--color-text-muted)", marginBottom: "1rem" }}>
            {isPrivacyMasked
              ? "Clumse AI answers from the privacy-safe project blueprint only. Street address and homeowner identity are withheld until award."
              : "Clumse AI uses the ticket description and property notes to answer questions before you submit your bid."}
          </p>
          <ChatScroll ref={chatScrollRef}>
            {negotiationLog.length === 0 ? (
              <TextMuted>
                {isPrivacyMasked
                  ? "Loading project blueprint…"
                  : "No messages yet. Say hello or ask about access, scope, or materials."}
              </TextMuted>
            ) : (
              negotiationLog.map((m, i) => (
                <ChatBubble key={`${m.createdAt || i}-${i}`} $role={m.role}>
                  <ChatRole>{formatVendorRole(m.role)}</ChatRole>
                  <VendorMessageBody
                    text={m.content}
                    isBlueprint={m.messageType === "blueprint" || (i === 0 && m.role === "leo")}
                  />
                </ChatBubble>
              ))
            )}
          </ChatScroll>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <TextArea
              rows={2}
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="e.g. Is the water shutoff accessible in-unit?"
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  sendLeoMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={chatSending}
              onClick={sendLeoMessage}
              style={{ alignSelf: "flex-end" }}
            >
              Send
            </Button>
          </div>
        </Card>

        {/* Bid Submission Form */}
        <Card title="Your Bid">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
            {/* Vendor Information (Read-only) */}
            <FormSection>
              <SectionTitle>Vendor Information</SectionTitle>
              <Form.Item label="Company Name" name="vendorName">
                <Input prefix={<UserOutlined />} disabled />
              </Form.Item>
              <Form.Item label="Email" name="vendorEmail">
                <Input prefix={<MailOutlined />} disabled />
              </Form.Item>
              <Form.Item
                label="Phone Number"
                name="vendorPhone"
                rules={[
                  { required: true, message: "Please enter your phone number" },
                  {
                    pattern: /^[0-9+\-() ]+$/,
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder="(555) 123-4567"
                />
              </Form.Item>
            </FormSection>

            {/* Bid Details */}
            <FormSection>
              <SectionTitle>Bid Details</SectionTitle>
              <Form.Item
                label="Bid Amount"
                name="bidAmount"
                rules={[
                  { required: true, message: "Please enter your bid amount" },
                  {
                    type: "number",
                    min: 0,
                    message: "Bid amount must be positive",
                  },
                ]}
              >
                <InputNumber
                  prefix="$"
                  style={{ width: "100%" }}
                  placeholder="0.00"
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                />
              </Form.Item>

              <Form.Item
                label="Estimated Hours (Optional)"
                name="estimatedHours"
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="e.g., 4"
                  min={0}
                  step={0.5}
                />
              </Form.Item>

              <Form.Item label="Timeline" name="timeline">
                <Select placeholder="Select estimated timeline">
                  <Option value="Within 24 hours">Within 24 hours</Option>
                  <Option value="1-2 days">1-2 days</Option>
                  <Option value="2-3 days">2-3 days</Option>
                  <Option value="3-5 days">3-5 days</Option>
                  <Option value="1 week">1 week</Option>
                  <Option value="1-2 weeks">1-2 weeks</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Additional Notes (Optional)" name="notes">
                <TextArea
                  rows={4}
                  placeholder="Describe your approach, materials to be used, warranty offered, or any other relevant details..."
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
            </FormSection>

            <Form.Item>
              <SubmitButton
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<CheckCircleOutlined />}
                block
                size="large"
              >
                Submit Bid
              </SubmitButton>
            </Form.Item>
          </Form>
        </Card>

        <Footer>
          <FooterText>
            After submitting your bid, the property owner will review it along
            with other bids. You'll be notified via email when a decision is
            made.
          </FooterText>
        </Footer>
      </Container>
        </MainCol>
        <AsideCol>
          <PreferredEdgePromo
            bidToken={token}
            returnPage="bid"
            isPreferred={isPreferredVendor}
            onPreferredChange={setIsPreferredVendor}
          />
        </AsideCol>
      </VendorBidLayout>
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding: 2rem 1rem;

  @media ${device.mobile} {
    padding: 1rem 0.5rem;
  }
`;

const VendorBidLayout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  justify-content: center;
  max-width: 1120px;
  margin: 0 auto;
  flex-wrap: wrap;
`;

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 800px;
`;

const AsideCol = styled.aside`
  width: 100%;
  max-width: 320px;
  flex-shrink: 0;
  position: sticky;
  top: 1rem;
  @media ${device.mobile} {
    position: static;
    max-width: none;
  }
`;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 1.5rem;
`;

const LoadingText = styled.div`
  font-size: 1.1rem;
  color: var(--color-text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const HeaderTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 1rem 0 0.5rem 0;
  font-family: var(--font-primary);

  @media ${device.mobile} {
    font-size: 1.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-muted);
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.95rem;
  }
`;

const ZipBadge = styled.div`
  display: inline-block;
  margin-top: 0.75rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const BlueprintPanel = styled.div`
  flex: 1;
  margin-top: 0.5rem;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  line-height: 1.55;
  font-size: 0.95rem;
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const ChatRole = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
  opacity: 0.85;
`;

const MessageBody = styled.div`
  white-space: pre-wrap;
  line-height: 1.55;
  font-size: ${(p) => (p.$isBlueprint ? "0.92rem" : "0.95rem")};
  overflow-wrap: anywhere;
`;

const TicketDetailRow = styled.div`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: var(--color-text);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;

  strong {
    min-width: 120px;
    color: var(--color-text);
  }

  @media ${device.mobile} {
    flex-direction: column;
    gap: 0.25rem;

    strong {
      min-width: auto;
    }
  }
`;

const Description = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: var(--color-text-muted);
  margin-top: 0.5rem;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 8px;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1.5rem;
  font-family: var(--font-primary);
`;

const SubmitButton = styled(Button)`
  height: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  background: var(--color-primary);
  border-color: var(--color-primary);

  &:hover {
    background: var(--color-primary-darker);
    border-color: var(--color-primary-darker);
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
`;

const ExistingBidSection = styled.div`
  padding: 1rem;
`;

const BidStatusRow = styled.div`
  margin-bottom: 1rem;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
`;

const BidDetailRow = styled.div`
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: var(--color-text-muted);

  strong {
    color: var(--color-text);
  }
`;

const ChatScroll = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const ChatBubble = styled.div`
  margin-bottom: 10px;
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(p) =>
    p.$role === "leo"
      ? "#e6f4ff"
      : p.$role === "vendor"
      ? "#f6ffed"
      : "#f5f5f5"};
`;

const TextMuted = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.95rem;
`;

const NegotiateLinkRow = styled.div`
  text-align: center;
  margin-bottom: 1.25rem;
  a {
    font-weight: 600;
    color: var(--color-primary);
  }
`;

export default VendorBidPage;
